import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';

/**
 * NumberTextField - A TextField component specifically designed for number inputs
 * that prevents the leading zero issue (e.g., "010" when typing "10")
 * 
 * This component:
 * - Allows empty string values while typing
 * - Prevents leading zeros when user types
 * - Falls back to the specified default value (usually 0) only when the field loses focus
 * - Maintains proper number formatting
 */

type NumberTextFieldProps = Omit<TextFieldProps, 'type'> & {
  onValueChange?: (value: number) => void;
  defaultValue?: number;
  allowNegative?: boolean;
  allowDecimal?: boolean;
};

export const NumberTextField: React.FC<NumberTextFieldProps> = ({
  value,
  onChange,
  onValueChange,
  onBlur,
  defaultValue = 0,
  allowNegative = false,
  allowDecimal = true,
  inputProps,
  name,
  ...rest
}) => {
  const [internalValue, setInternalValue] = React.useState<string>(() => {
    // Initialize with the value or empty string if it's 0
    if (value === 0 || value === '0') {
      return '';
    }
    return String(value ?? '');
  });

  // Update internal value when prop value changes (for controlled components)
  React.useEffect(() => {
    if (value !== undefined && value !== null) {
      // Only update if different and not currently being edited
      const numValue = typeof value === 'number' ? value : parseFloat(String(value));
      if (!isNaN(numValue) && numValue !== 0) {
        setInternalValue(String(value));
      } else if (numValue === 0) {
        setInternalValue('');
      }
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    // Allow empty string
    if (inputValue === '') {
      setInternalValue('');
      if (onChange) {
        onChange(e);
      }
      if (onValueChange) {
        onValueChange(defaultValue);
      }
      return;
    }

    // Validate the input based on settings
    const decimalRegex = allowDecimal
      ? allowNegative
        ? /^-?\d*\.?\d*$/
        : /^\d*\.?\d*$/
      : allowNegative
      ? /^-?\d*$/
      : /^\d*$/;

    if (decimalRegex.test(inputValue)) {
      // Remove leading zeros unless it's a decimal like "0.5"
      if (inputValue.length > 1 && inputValue[0] === '0' && inputValue[1] !== '.') {
        inputValue = inputValue.replace(/^0+/, '');
      }

      // Handle negative zero
      if (inputValue === '-0' || inputValue === '-') {
        setInternalValue(inputValue);
      } else {
        setInternalValue(inputValue);
      }

      // Create a synthetic event with the cleaned value and explicit name (spreading e.target can miss name in some environments)
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name: name ?? e.target?.name,
          value: inputValue,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      if (onChange) {
        onChange(syntheticEvent);
      }

      if (onValueChange) {
        const numValue = parseFloat(inputValue);
        onValueChange(isNaN(numValue) ? defaultValue : numValue);
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // On blur, if the field is empty or invalid, set it to the default value
    if (internalValue === '' || internalValue === '-' || internalValue === '.') {
      setInternalValue(defaultValue === 0 ? '' : String(defaultValue));
      
      // Trigger onChange with the default value and explicit name
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name: name ?? (e.target as HTMLInputElement)?.name,
          value: String(defaultValue),
        },
      } as any;

      if (onChange) {
        onChange(syntheticEvent);
      }

      if (onValueChange) {
        onValueChange(defaultValue);
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <TextField
      {...rest}
      name={name}
      type="number"
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{
        ...inputProps,
        inputMode: allowDecimal ? 'decimal' : 'numeric',
      }}
    />
  );
};

export default NumberTextField;
